import { useState, useEffect } from 'react'
import { PlusIcon, PencilSquareIcon, TrashIcon, ChevronRightIcon, ChevronDownIcon, FolderIcon, FolderOpenIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'

const EquipmentCategoriesPage = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState(new Set())
  const [formData, setFormData] = useState({
    name: '',
    parent_id: null
  })

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/auxiliary-equipment/categories')

      // API retorna { data: { flat: [...], tree: [...] } }
      const apiData = response.data.data || response.data

      // Se apiData tem propriedade 'flat', usar ela; senão assumir que já é array
      const categoriesArray = apiData?.flat || apiData || []

      setCategories(Array.isArray(categoriesArray) ? categoriesArray : [])

      console.log(`✅ ${categoriesArray.length} categorias carregadas`)
    } catch (error) {
      console.error('Erro ao carregar categorias:', error)
      toast.error('Erro ao carregar categorias')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (editingCategory) {
        // Atualizar categoria existente
        await api.put(`/api/auxiliary-equipment/categories/${editingCategory.id}`, formData)
        toast.success('Categoria atualizada com sucesso!')
      } else {
        // Criar nova categoria
        await api.post('/api/auxiliary-equipment/categories', formData)
        toast.success('Categoria criada com sucesso!')
      }

      setShowModal(false)
      resetForm()
      loadCategories()
    } catch (error) {
      console.error('Erro ao salvar categoria:', error)
      toast.error(error.response?.data?.message || 'Erro ao salvar categoria')
    }
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      parent_id: category.parent_id
    })
    setShowModal(true)
  }

  const handleDelete = async (categoryId) => {
    if (!confirm('Tem certeza que deseja excluir esta categoria? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      await api.delete(`/api/auxiliary-equipment/categories/${categoryId}`)
      toast.success('Categoria excluída com sucesso!')
      loadCategories()
    } catch (error) {
      console.error('Erro ao excluir categoria:', error)
      toast.error(error.response?.data?.message || 'Erro ao excluir categoria')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      parent_id: null
    })
    setEditingCategory(null)
  }

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId)
      } else {
        newSet.add(categoryId)
      }
      return newSet
    })
  }

  const getParentCategories = () => {
    return categories.filter(cat => !cat.parent_id)
  }

  const getSubCategories = (parentId) => {
    return categories.filter(cat => cat.parent_id === parentId)
  }

  const renderCategory = (category, level = 0) => {
    const subCategories = getSubCategories(category.id)
    const hasChildren = subCategories.length > 0
    const isExpanded = expandedCategories.has(category.id)

    return (
      <div key={category.id} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
        <div
          className={`flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
            level > 0 ? 'ml-' + (level * 8) : ''
          }`}
          style={{ paddingLeft: `${level * 32 + 16}px` }}
        >
          <div className="flex items-center gap-3 flex-1">
            {hasChildren ? (
              <button
                onClick={() => toggleCategoryExpansion(category.id)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-5 h-5" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5" />
                )}
              </button>
            ) : (
              <div className="w-5" />
            )}

            {level === 0 ? (
              isExpanded && hasChildren ? (
                <FolderOpenIcon className="w-6 h-6 text-blue-500" />
              ) : (
                <FolderIcon className="w-6 h-6 text-blue-500" />
              )
            ) : (
              <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500" />
            )}

            <div>
              <h3 className={`${level === 0 ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                {category.name}
              </h3>
              {level === 0 && hasChildren && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {subCategories.length} subcategoria{subCategories.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEdit(category)}
              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              title="Editar categoria"
            >
              <PencilSquareIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleDelete(category.id)}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title="Excluir categoria"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Subcategorias */}
        {isExpanded && hasChildren && (
          <div>
            {subCategories.map(subCat => renderCategory(subCat, level + 1))}
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Carregando categorias...</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Categorias de Equipamentos
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerencie as categorias e subcategorias de equipamentos auxiliares
          </p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Categoria
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total de Categorias</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{categories.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Categorias Principais</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{getParentCategories().length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Subcategorias</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {categories.length - getParentCategories().length}
          </div>
        </div>
      </div>

      {/* Lista de Categorias */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {getParentCategories().length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Nenhuma categoria cadastrada ainda.
          </div>
        ) : (
          <div>
            {getParentCategories().map(category => renderCategory(category))}
          </div>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome da Categoria *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Tanques e Reservatórios"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categoria Pai (opcional)
                </label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Nenhuma (Categoria Principal)</option>
                  {getParentCategories()
                    .filter(cat => !editingCategory || cat.id !== editingCategory.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Selecione uma categoria pai para criar uma subcategoria
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingCategory ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default EquipmentCategoriesPage
